import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function truncateAddress(addr: string, head = 4, tail = 4): string {
  if (!addr) return '';
  if (addr.length <= head + tail + 2) return addr;
  const prefix = addr.startsWith('0x') ? '0x' : '';
  const body = addr.startsWith('0x') ? addr.slice(2) : addr;
  return `${prefix}${body.slice(0, head)}…${body.slice(-tail)}`;
}

export function formatPoints(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  const abs = Math.abs(diffSec);
  const past = diffSec >= 0;
  const fmt = (value: number, unit: string): string => {
    const plural = value === 1 ? '' : 's';
    return past ? `${value} ${unit}${plural} ago` : `in ${value} ${unit}${plural}`;
  };
  if (abs < 60) return past ? 'just now' : 'in a moment';
  if (abs < 3600) return fmt(Math.floor(abs / 60), 'minute');
  if (abs < 86400) return fmt(Math.floor(abs / 3600), 'hour');
  if (abs < 2592000) return fmt(Math.floor(abs / 86400), 'day');
  if (abs < 31536000) return fmt(Math.floor(abs / 2592000), 'month');
  return fmt(Math.floor(abs / 31536000), 'year');
}

/** "84 KB" below 0.1 MB (where "0.1 MB" reads as suspiciously imprecise), else "1.4 MB". */
export function formatFileSize(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb < 0.1) return `${Math.round(bytes / 1024)} KB`;
  return `${mb.toFixed(1)} MB`;
}

/** Compact "2h 14m" / "14m" / "45s" countdown, floored at 0. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}
